import { DatePipe, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, afterNextRender, booleanAttribute, computed, inject, input, numberAttribute, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { interval, timer } from 'rxjs';
import { ConsultingReservationService } from 'src/app/components/consulting-reservation/consulting-reservation.service';
import { MapDialogComponent } from 'src/app/components/consulting-reservation/map-dialog/map-dialog.component';
import { ScheduleService } from 'src/app/features/schedule';
import { Schedule } from 'src/app/features/schedule/schedule.types';

interface DateData {
  date: string | Date;
  time: string;
}

@Component({
  selector: 'app-step1',
  imports: [
    DatePipe,
    SlicePipe,
    FormsModule,
    MatDialogModule,
    MatRadioModule
  ],
  templateUrl: './step1.component.html',
  styleUrls: ['./step1.component.scss'],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Step1Component {
  readonly #datePipe = inject(DatePipe);
  readonly #dialog = inject(MatDialog);
  readonly #scheduleService = inject(ScheduleService);
  readonly #reservationService = inject(ConsultingReservationService);

  protected readonly place = signal<string>('新宿オフィス');
  protected readonly schedules = signal<Schedule[]>([]);
  protected readonly selectedDate = signal<DateData | null>(null);
  protected readonly sending = signal(false);
  protected readonly canPrev = computed<boolean>(() => {
    const schedules = this.schedules();
    if (schedules.length === 0) {
      return false;
    }
    const date = new Date(this.schedules()[0].day);
    return date >= new Date();
  });
  protected readonly expirationCountdown = signal<string>('10:00');

  readonly bannerPc = input<string | null>(null);
  readonly bannerSp = input<string | null>(null);
  readonly lpType = input<string | null>(null);
  readonly hasComplete = input(false, { transform: booleanAttribute });
  readonly discount = input.required({ transform: numberAttribute });
  readonly isCasual = input.required<boolean>();
  readonly isColorful = input.required<boolean>();

  readonly selectDate = output<Date>();

  readonly #baseTime = new Date();

  constructor() {
    const session = sessionStorage.getItem('counseling');
    let date: string | undefined = undefined;
    if (session) {
      const value = JSON.parse(session);
      if (value.place) {
        this.place.set(value.place);
      }
      if (value.startTime) {
        date = this.#datePipe.transform(new Date(value.startTime), 'yyyy-MM-dd')!;
      }
      timer(30)
        .subscribe(() => {
          const element = document.querySelector('#schedules') as HTMLElement;
          element?.scrollIntoView({ behavior: 'instant', block: 'center' });
        });
    }

    afterNextRender(() => {
      if (this.isCasual()) {
        this.place.set('カジュアル面談');
      }
      this.#scheduleService.get(date, this.isCasual())
        .subscribe(res => {
          this.schedules.set(res);
        });
    });

    interval(1000)
      .pipe(
        takeUntilDestroyed()
      ).subscribe({
        next: x => {
          this.#countDown(x);
        }
      });
  }

  #countDown(count: number) {
    const now = new Date();
    const diff = Math.min(Math.floor((now.getTime() - this.#baseTime.getTime()) / 1000), 60 * 10);

    const dt = new Date(0, 0, 0, 0, 10, 0);
    dt.setTime(dt.getTime() - diff * 1000);

    const minutes = dt.getMinutes().toString().padStart(2, '0');
    const seconds = dt.getSeconds().toString().padStart(2, '0');
    this.expirationCountdown.set(`${minutes}:${seconds}`);
  }

  protected prevWeek(): void {
    if (!this.canPrev()) {
      return;
    }
    const date = new Date(this.schedules()[0].day);
    const target = this.#datePipe.transform(date.setDate(date.getDate() - 7), 'yyyy-MM-dd')!;
    this.#scheduleService.get(target)
      .subscribe(res => {
        this.schedules.set(res);
      });
  }

  protected nextWeek(): void {
    const date = new Date(this.schedules()[0].day);
    const target = this.#datePipe.transform(date.setDate(date.getDate() + 7), 'yyyy-MM-dd')!;
    this.#scheduleService.get(target)
      .subscribe(res => {
        this.schedules.set(res);
      });
  }

  protected openMap(): void {
    this.#dialog.open(MapDialogComponent, {
      width: '86rem',
      maxWidth: '98%',
      maxHeight: '95dvh',

    });
  }

  protected select(event: MouseEvent, target: string | Date, time: string): void {
    event.preventDefault();
    event.stopImmediatePropagation();
    const times = time.split(':');
    let startTime = new Date(target);
    startTime.setHours(Number(times[0]));
    startTime.setMinutes(Number(times[1]));
    const item = sessionStorage.getItem('counseling');
    const value = item ? JSON.parse(item) : {};
    Object.assign(value, { startTime, place: this.place(), top: window.scrollY, lpType: this.lpType(), isCasual: this.isCasual() });
    sessionStorage.setItem('counseling', JSON.stringify(value));
    if (!this.hasComplete()) {
      this.#reservationService.next('step2');
      return;
    }
    this.selectedDate.set({ date: target, time });
  }

  protected submit() {
    const value = JSON.parse(sessionStorage.getItem('counseling') ?? '{}');
    this.selectDate.emit(new Date(value.startTime));
    this.sending.set(true);
    timer(5000)
      .subscribe(() => {
        this.sending.set(false);
      });
  }

  back(): void {
    this.#reservationService.back();
  }
}
